/**
 * Tiny axios wrapper for the public admissions surface.
 *
 * Why a separate client (instead of reusing `@eduviaos/shared/api`):
 *   - The shared client carries auth interceptors (refresh-cookie
 *     dance, 401 handling) that are meaningless on the public surface
 *     and would just bloat the bundle for parents.
 *   - Public requests must NEVER send credentials by default — `withCredentials`
 *     is omitted explicitly so a stray HttpOnly cookie set by another
 *     EduViaOS subdomain can't accidentally leak into our requests.
 *   - We want the BE's `Cache-Control: no-store` to dominate any
 *     intermediate proxy; setting `Cache-Control: no-cache` on the
 *     request side reinforces that.
 *
 * Tenant resolution (D2d):
 *   The slug NO LONGER appears on the URL path. Instead, every request
 *   carries an `X-Tenant-Slug` header sourced from the host name (via
 *   the shared `getCurrentTenant()` helper, which understands the
 *   `<slug>--apply-{stage,pr-N}` shape and the production
 *   `<slug>.eduviaos.com` shape). The Worker AT the edge OVERWRITES
 *   this header with the host-derived slug before forwarding to the
 *   BE, so a malicious browser session can't escape its own subdomain
 *   even if it tries to forge the header.
 */
import axios, { type AxiosInstance } from "axios";
import { getCurrentTenant } from "@eduviaos/shared";
import type {
  PublicApplicantAttemptResponse,
  PublicApplyFormResponse,
  PublicApplyFormSubmitRequest,
  PublicApplyFormSubmitResponse,
  PublicApplyMeResponse,
  PublicApplyStatusResponse,
  PublicCommitProctorUploadResponse,
  PublicIntakeFormResponse,
  PublicMintProctorUploadRequest,
  PublicMintProctorUploadResponse,
  PublicRecordProctorEventRequest,
  PublicRecordProctorEventResponse,
  PublicResultReport,
  PublicSaveResponseRequest,
  PublicSubmitAttemptResponse,
  PublicSubmitRequest,
  PublicSubmitResponse,
  PublicTrackResponse,
} from "@eduviaos/shared/types";

const baseURL = import.meta.env.VITE_API_URL ?? "/api";

export const publicApi: AxiosInstance = axios.create({
  baseURL,
  // No credentials — anonymous surface only.
  withCredentials: false,
  headers: {
    "Cache-Control": "no-cache",
    "X-Eduviaos-Surface": "public-admissions",
  },
  // Generous-but-bounded timeout: parent intake forms sometimes carry
  // 10–20 fields with attachments in the future slice; the current
  // text-only form fits comfortably under 5s on a 3G connection.
  timeout: 15_000,
});

// Inject the current tenant slug on every request. Resolving lazily
// (per request, not on client construction) means the same SPA build
// works on every `<slug>--apply-stage.eduviaos.com` host without a
// rebuild — and a future SPA-controlled tenant switcher would just
// need to mutate `getCurrentTenant`'s storage.
publicApi.interceptors.request.use((config) => {
  const slug = getCurrentTenant().slug;
  if (slug) {
    config.headers = config.headers ?? {};
    config.headers["X-Tenant-Slug"] = slug;
  }
  // Deliberately DO NOT throw when slug is null — the BE returns the
  // generic 404 envelope for missing-tenant signal, and the page
  // components render a "Open your institution's link" empty state.
  // Throwing here would be a layering violation: api client would be
  // making routing decisions about a UX state.
  return config;
});

// ── Form discovery ──────────────────────────────────────────────────────

export async function fetchIntakeForm(): Promise<PublicIntakeFormResponse> {
  const { data } = await publicApi.get<PublicIntakeFormResponse>("/public/admissions/intake-form");
  return data;
}

// ── Submission ──────────────────────────────────────────────────────────

export async function submitEnquiry(body: PublicSubmitRequest): Promise<PublicSubmitResponse> {
  const { data } = await publicApi.post<PublicSubmitResponse>("/public/admissions/enquiries", body);
  return data;
}

// ── Tracking ────────────────────────────────────────────────────────────

export async function trackEnquiry(token: string): Promise<PublicTrackResponse> {
  const { data } = await publicApi.get<PublicTrackResponse>(
    `/public/admissions/track/${encodeURIComponent(token)}`,
  );
  return data;
}

// ── Phase F4 — Apply portal (single-use parent-facing tokens) ──────────
//
// All nine public apply-portal endpoints live under `/public/admissions/apply/`
// on the BE (see `app/domains/admission/public_routes.py`). Same X-Tenant-Slug
// header semantics as the rest of this surface — the Worker enforces the
// correct slug at the edge, so we just forward whatever the page resolved
// from the host.

function applyEncodeToken(token: string): string {
  // RFC 3986 unreserved-only — encode aggressively to be safe even if a
  // legacy token format ever leaks `+` / `/` into the URL.
  return encodeURIComponent(token);
}

/** GET /apply/{token}/me — discovery (purpose, stage, expiry). */
export async function fetchApplyMe(token: string): Promise<PublicApplyMeResponse> {
  const { data } = await publicApi.get<PublicApplyMeResponse>(
    `/public/admissions/apply/${applyEncodeToken(token)}/me`,
  );
  return data;
}

/** GET /apply/{token}/form — fetch the form linked to the current stage. */
export async function fetchApplyForm(token: string): Promise<PublicApplyFormResponse> {
  const { data } = await publicApi.get<PublicApplyFormResponse>(
    `/public/admissions/apply/${applyEncodeToken(token)}/form`,
  );
  return data;
}

/** POST /apply/{token}/form/submit — consume token and submit the form. */
export async function submitApplyForm(
  token: string,
  body: PublicApplyFormSubmitRequest,
): Promise<PublicApplyFormSubmitResponse> {
  const { data } = await publicApi.post<PublicApplyFormSubmitResponse>(
    `/public/admissions/apply/${applyEncodeToken(token)}/form/submit`,
    body,
  );
  return data;
}

/** GET /apply/{token}/assessment — slim attempt DTO (PII-redacted). */
export async function fetchApplyAttempt(token: string): Promise<PublicApplicantAttemptResponse> {
  const { data } = await publicApi.get<PublicApplicantAttemptResponse>(
    `/public/admissions/apply/${applyEncodeToken(token)}/assessment`,
  );
  return data;
}

/** POST /apply/{token}/assessment:start — flips status to `in_progress`. */
export async function startApplyAttempt(token: string): Promise<PublicApplicantAttemptResponse> {
  const { data } = await publicApi.post<PublicApplicantAttemptResponse>(
    `/public/admissions/apply/${applyEncodeToken(token)}/assessment:start`,
  );
  return data;
}

/** POST /apply/{token}/assessment:save-response — autosave a single answer. */
export async function saveApplyResponse(
  token: string,
  body: PublicSaveResponseRequest,
): Promise<PublicApplicantAttemptResponse> {
  const { data } = await publicApi.post<PublicApplicantAttemptResponse>(
    `/public/admissions/apply/${applyEncodeToken(token)}/assessment:save-response`,
    body,
  );
  return data;
}

/** POST /apply/{token}/assessment:submit — final, irreversible submit. */
export async function submitApplyAttempt(token: string): Promise<PublicSubmitAttemptResponse> {
  const { data } = await publicApi.post<PublicSubmitAttemptResponse>(
    `/public/admissions/apply/${applyEncodeToken(token)}/assessment:submit`,
  );
  return data;
}

/** GET /apply/{token}/status — reusable parent-facing status page. */
export async function fetchApplyStatus(token: string): Promise<PublicApplyStatusResponse> {
  const { data } = await publicApi.get<PublicApplyStatusResponse>(
    `/public/admissions/apply/${applyEncodeToken(token)}/status`,
  );
  return data;
}

/** GET /apply/{token}/result — full result report for most recent graded attempt. */
export async function fetchApplyResult(token: string): Promise<PublicResultReport> {
  const { data } = await publicApi.get<PublicResultReport>(
    `/public/admissions/apply/${applyEncodeToken(token)}/result`,
  );
  return data;
}

// ── Phase F11 — Public proctor pipeline ────────────────────────────────
//
// Three endpoints for the proctor SDK. The browser PUTs artifact bytes
// DIRECTLY to the storage bucket via the signed URL minted in step 1;
// step 2 confirms via the BE; step 3 is just a JSON event log. The
// BE never sees the bytes — the API tier stays slim under bursty
// per-attempt capture cadences.

/** POST /apply/{token}/proctor-artifact:mint-upload — request a signed PUT URL. */
export async function mintProctorUploadUrl(
  token: string,
  body: PublicMintProctorUploadRequest,
): Promise<PublicMintProctorUploadResponse> {
  const { data } = await publicApi.post<PublicMintProctorUploadResponse>(
    `/public/admissions/apply/${applyEncodeToken(token)}/proctor-artifact:mint-upload`,
    body,
  );
  return data;
}

/** POST /apply/{token}/proctor-artifact/{id}:commit — confirm the bytes landed. */
export async function commitProctorUpload(
  token: string,
  artifactId: string,
): Promise<PublicCommitProctorUploadResponse> {
  const { data } = await publicApi.post<PublicCommitProctorUploadResponse>(
    `/public/admissions/apply/${applyEncodeToken(token)}/proctor-artifact/${encodeURIComponent(
      artifactId,
    )}:commit`,
  );
  return data;
}

/** POST /apply/{token}/proctor-event — record one lifecycle event. */
export async function recordProctorEvent(
  token: string,
  body: PublicRecordProctorEventRequest,
): Promise<PublicRecordProctorEventResponse> {
  const { data } = await publicApi.post<PublicRecordProctorEventResponse>(
    `/public/admissions/apply/${applyEncodeToken(token)}/proctor-event`,
    body,
  );
  return data;
}

/**
 * Stream artifact bytes to the bucket via the signed URL. Used by the
 * F11 proctor SDK after `mintProctorUploadUrl`.
 *
 * NOT routed through `publicApi` because:
 *   - the upload URL is bucket-side (GCS signed URL or local
 *     `/__local_uploads__/<path>` in dev); it isn't an EduViaOS API
 *     endpoint and shouldn't carry our `X-Tenant-Slug` /
 *     `X-EduViaOS-Surface` headers.
 *   - the bucket signature is locked to the EXACT headers minted by
 *     `MintProctorUploadResponse.required_headers`; adding our own
 *     would invalidate the signature.
 *   - the body is binary (Blob) and benefits from streaming — axios
 *     transforms would fight the browser's native Blob path.
 */
export async function uploadProctorArtifactBytes(
  putUrl: string,
  blob: Blob,
  requiredHeaders: Record<string, string>,
): Promise<void> {
  const response = await fetch(putUrl, {
    method: "PUT",
    body: blob,
    headers: requiredHeaders,
    // No credentials — the signed URL IS the auth.
    credentials: "omit",
    // Bucket-side endpoint; no caching.
    cache: "no-store",
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      `Proctor upload failed (HTTP ${response.status})${detail ? `: ${detail.slice(0, 200)}` : ""}`,
    );
  }
}
